import React, { useEffect, useState } from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ConfirmationProvider } from './contexts/ConfirmationContext';
import { NavigationSafetyProvider } from './contexts/NavigationSafetyContext';
import { AuthService } from './services/authService';
import { User } from './types';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ArticleWizardPage } from './pages/ArticleWizardPage';
import { ArticleListPage } from './pages/ArticleListPage';
import { ArticleDetailPage } from './pages/ArticleDetailPage';
import { EditArticlePage } from './pages/EditArticlePage';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { AllArticlesPage } from './pages/AllArticlesPage';
import { BooksPage } from './pages/BooksPage';
import { ReviewsPage } from './pages/ReviewsPage';
import UmbruchentfernerPage from './pages/UmbruchentfernerPage';
import SocialMediaPage from './pages/SocialMediaPage';
import EinblendungJetztPage from './pages/EinblendungJetztPage';
import KIAuthorsPage from './pages/KIAuthorsPage';
import AdminPage from './pages/AdminPage';
import NewsletterPage from './pages/NewsletterPage';
import DachthekenduettPage from './pages/DachthekenduettPage';
import ArtikelExtraktorPage from './pages/ArtikelExtraktorPage';
import { Layout } from './components/layout/Layout';

// Geschützte Route-Komponente
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Ein Data Router ermöglicht zuverlässige Navigationssperren, auch beim Browser-Zurück.
const router = createBrowserRouter(createRoutesFromElements(
  <>
    {/* Öffentliche Routen */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />

    {/* Geschützte Routen */}
    <Route path="/" element={
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    }>
      <Route index element={<Navigate to="/dashboard" replace />} />
      <Route path="dashboard" element={<DashboardPage />} />
      <Route path="articles" element={<ArticleListPage />} />
      <Route path="all-articles" element={<AllArticlesPage />} />
      <Route path="articles/:id" element={<ArticleDetailPage />} />
      <Route path="articles/:id/edit" element={<EditArticlePage />} />
      <Route path="wizard" element={<ArticleWizardPage />} />
      <Route path="books" element={<BooksPage />} />
      <Route path="reviews" element={<ReviewsPage />} />
      <Route path="umbruchentferner" element={<UmbruchentfernerPage />} />
      <Route path="social-media" element={<SocialMediaPage />} />
      <Route path="einblendung-jetzt" element={<EinblendungJetztPage />} />
      <Route path="ki-autoren" element={<KIAuthorsPage />} />
      <Route path="newsletter" element={<NewsletterPage />} />
      <Route path="dachthekenduett" element={<DachthekenduettPage />} />
      <Route path="artikel-extraktor" element={<ArtikelExtraktorPage />} />
      <Route path="settings" element={<UserSettingsPage />} />
      <Route path="admin" element={<AdminPage />} />
    </Route>

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/dashboard" replace />} />

  </>
));

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuthenticated = await AuthService.isAuthenticated();
        if (isAuthenticated) {
          const currentUser = await AuthService.getCurrentUser();
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AuthProvider user={user} setUser={setUser}>
      <div className="min-h-screen bg-gray-50">
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />

        <NavigationSafetyProvider><ConfirmationProvider><RouterProvider router={router} /></ConfirmationProvider></NavigationSafetyProvider>
      </div>
    </AuthProvider>
  );
}

export default App;
